import React, { useState, useCallback } from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CloudUpload, PhotoCamera, Error } from '@mui/icons-material';
import { en } from '../translations/en';
import { SUPPORTED_FORMATS, ACCEPT_EXTENSIONS, isRawFileByExtension } from '../utils/uploadUtils';
import { MAX_IMAGE_FILE_SIZE, MAX_IMAGES_PER_ITEM } from '../utils/uploadConstants';

interface ValidationError {
  file: File;
  error: string;
}

interface ImageUploadAreaProps {
  onFileUpload: (files: File[]) => void;
  onValidationError?: (errors: ValidationError[]) => void;
  validationErrors?: ValidationError[];
  maxFileSize?: number;
  isUploading?: boolean;
  /** Number of images already attached to the catalog item (used to enforce maxImages). */
  currentImageCount?: number;
  /** Maximum total images allowed per catalog item. Defaults to MAX_IMAGES_PER_ITEM. */
  maxImages?: number;
  /**
   * When true, renders a tighter dropzone (smaller icon, less padding,
   * support text on a single line). Used by the redesigned edit-object
   * flat layout; the legacy accordion layout leaves it off.
   */
  compact?: boolean;
  'data-testid'?: string;
}

export const ImageUploadArea = ({
  onFileUpload,
  onValidationError,
  validationErrors = [],
  maxFileSize = MAX_IMAGE_FILE_SIZE,
  isUploading = false,
  currentImageCount = 0,
  maxImages = MAX_IMAGES_PER_ITEM,
  compact = false,
  'data-testid': testId
}: ImageUploadAreaProps) => {
  const theme = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);

  const validateFiles = useCallback((files: FileList | File[]): { valid: File[], invalid: ValidationError[] } => {
    const fileArray = Array.from(files);
    const valid: File[] = [];
    const invalid: ValidationError[] = [];
    const remainingSlots = Math.max(0, maxImages - currentImageCount);

    fileArray.forEach((file, index) => {
      // Enforce per-item image cap (default 20, per Updated-upload-object mockup).
      if (index >= remainingSlots) {
        invalid.push({
          file,
          error: `Maximum of ${maxImages} images per object reached. Remove existing images before adding more.`,
        });
        return;
      }
      // Check file format — SUPPORTED_FORMATS is a Set, use .has() (#1040)
      // Fallback to extension check for RAW files (browsers report empty MIME type)
      const isSupported = SUPPORTED_FORMATS.has(file.type) || (!file.type && isRawFileByExtension(file.name));
      if (!isSupported) {
        invalid.push({
          file,
          error: `Unsupported format: ${file.type}. Supported formats: JPEG, PNG, HEIC, WebP, GIF, TIFF, RAW`
        });
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
        const fileSizeMB = Math.round(file.size / (1024 * 1024));
        invalid.push({
          file,
          error: `File size ${fileSizeMB}MB exceeds maximum ${maxSizeMB}MB`
        });
        return;
      }

      valid.push(file);
    });

    return { valid, invalid };
  }, [maxFileSize, maxImages, currentImageCount]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const { valid, invalid } = validateFiles(files);

    if (valid.length > 0) {
      onFileUpload(valid);
    }

    if (invalid.length > 0 && onValidationError) {
      onValidationError(invalid);
    }

    // Reset input value to allow selecting the same files again
    event.target.value = '';
  }, [validateFiles, onFileUpload, onValidationError]);

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set dragOver to false if we're leaving the drop zone itself
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    const { valid, invalid } = validateFiles(files);

    if (valid.length > 0) {
      onFileUpload(valid);
    }

    if (invalid.length > 0 && onValidationError) {
      onValidationError(invalid);
    }
  }, [validateFiles, onFileUpload, onValidationError]);

  return (
    <Box>
      <Paper
        sx={{
          p: compact ? 2.5 : 4,
          border: '2px dashed',
          borderColor: isDragOver ? theme.palette.primary.main : theme.palette.divider,
          bgcolor: isDragOver ? theme.palette.action.hover : 'transparent',
          textAlign: 'center',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            bgcolor: theme.palette.action.hover
          },
          ...(isDragOver && {
            transform: 'scale(1.02)',
            boxShadow: theme.shadows[4]
          })
        }}
        component="label"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={isDragOver ? 'drag-over' : ''}
        data-testid={testId}
        data-is-uploading={isUploading}
      >
        <input
          type="file"
          multiple
          accept={[...SUPPORTED_FORMATS].join(',') + ',' + ACCEPT_EXTENSIONS}
          onChange={handleFileChange}
          disabled={isUploading}
          style={{ display: 'none' }}
        />
        <CloudUpload
          sx={{
            fontSize: compact ? 32 : 48,
            color: isDragOver ? theme.palette.primary.main : theme.palette.text.secondary,
            mb: compact ? 1 : 2,
            transition: 'color 0.2s ease-in-out'
          }}
        />
        <Typography variant={compact ? 'subtitle1' : 'h6'} gutterBottom={!compact} sx={{ mb: compact ? 0.5 : undefined }}>
          {isDragOver ? en.wizard.upload.addImages.dropHere : en.wizard.upload.addImages.dropText}
        </Typography>
        {!compact && (
          <Typography variant="body2" color="text.secondary">
            {en.wizard.upload.addImages.supportText}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: compact ? 0 : 1 }}>
          {en.wizard.upload.addImages.supportedFormats.replace('{maxSize}', Math.round(maxFileSize / (1024 * 1024)).toString())}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: compact ? 1 : 2 }}>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhotoCamera fontSize="small" />
            {en.wizard.upload.addImages.camera}
          </Typography>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CloudUpload fontSize="small" />
            {en.wizard.upload.addImages.files}
          </Typography>
        </Box>
      </Paper>

      {validationErrors.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {validationErrors.map((error, index) => (
            <Alert
              key={index}
              severity="error"
              icon={<Error />}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2">
                <strong>{error.file.name}:</strong> {error.error}
              </Typography>
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
};
